export const ErrorType = {
    ChunkLoadError: "ChunkLoadError",
    NetworkError: "NetworkError",
    APIError: "APIError",
    ComponentError: "ComponentError",
    UnknownError: "UnknownError",
} as const;

export type ErrorType = (typeof ErrorType)[keyof typeof ErrorType];

export interface ClassifiedError {
    type: ErrorType;
    message: string;
    isRetryable: boolean;
    requiresReload: boolean;
    originalError: Error;
}

export function classifyError(error: Error): ClassifiedError {
    const message = error.message.toLowerCase();

    // Check for chunk loading errors (common during deployments)
    if (
        message.includes("loading chunk") ||
        message.includes("dynamically imported module") ||
        message.includes("loading css chunk") ||
        error.name === "ChunkLoadError"
    ) {
        return {
            type: ErrorType.ChunkLoadError,
            message: "We're updating the app. Please refresh to get the latest version.",
            isRetryable: true,
            requiresReload: true,
            originalError: error,
        };
    }

    // Check for network errors
    if (
        message.includes("fetch") ||
        message.includes("network") ||
        message.includes("connection") ||
        (error.name === "TypeError" && message.includes("failed to fetch"))
    ) {
        return {
            type: ErrorType.NetworkError,
            message: "Network connection issue. Please check your internet and try again.",
            isRetryable: true,
            requiresReload: false,
            originalError: error,
        };
    }

    // Check for API errors (handled elsewhere but good to classify)
    if (message.includes("api") || message.includes("server")) {
        return {
            type: ErrorType.APIError,
            message: "Server temporarily unavailable. Please try again in a moment.",
            isRetryable: true,
            requiresReload: false,
            originalError: error,
        };
    }

    // Component-specific errors
    if (message.includes("render") || message.includes("component")) {
        return {
            type: ErrorType.ComponentError,
            message: "Something went wrong with this section. Let's try reloading it.",
            isRetryable: true,
            requiresReload: false,
            originalError: error,
        };
    }

    // Fallback for unknown errors
    return {
        type: ErrorType.UnknownError,
        message: "Something unexpected happened. Our team has been notified.",
        isRetryable: true,
        requiresReload: false,
        originalError: error,
    };
}

export function shouldAutoRetry(classifiedError: ClassifiedError): boolean {
    return classifiedError.type === ErrorType.ChunkLoadError || classifiedError.type === ErrorType.NetworkError;
}

export function getRetryDelay(attemptNumber: number): number {
    // Exponential backoff: 1s, 2s, 4s
    return Math.min(1000 * Math.pow(2, attemptNumber - 1), 4000);
}
